import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { trackCxFoundationsL1IssueClick } from "@faire/web-api/events/cxFoundations/click/l1Issue";
import { trackCxFoundationsTopicSelectPageView } from "@faire/web-api/events/cxFoundations/view/topicSelectPage";
import { QueryParameters as supportSelfServeParams } from "@faire/web-api/routes/support/self-serve";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect } from "react";
import styled from "styled-components";
import { IssueTopics } from "../../components/IssueTopics";
import { useClearNodesInLocalStorageOnLoad } from "../../hooks/useClearNodesInLocalStorageOnLoad";
import { useNavigation } from "../../hooks/useNavigation";
import { useNodeSetter } from "../../hooks/useNodeSetter";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useSkipNodeSelection } from "../../hooks/useSkipNodeSelection";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { useTree } from "../../hooks/useTree";
var PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
export var Home = function Home(_ref) {
  var isLoading = _ref.isLoading,
    dataUpdatedAt = _ref.dataUpdatedAt;
  useTrackPageViewEffect(trackCxFoundationsTopicSelectPageView);
  useClearNodesInLocalStorageOnLoad();
  var _useTree = useTree(),
    tree = _useTree.tree;
  var _useSelectedNode = useSelectedNode(),
    level1Node = _useSelectedNode.level1Node;
  var _useNodeSetter = useNodeSetter(),
    setLevel1Node = _useNodeSetter.setLevel1Node;
  var _useNavigation = useNavigation(),
    goToNextStep = _useNavigation.goToNextStep;
  var goToNextStepRef = React.useRef(goToNextStep);
  goToNextStepRef.current = goToNextStep;
  var retailerOrBrandToken = getRetailerOrBrandToken();
  var loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_self_serve_home",
    enableTrackPerformanceBreakdown: "internal-tracking",
    isPageLoading: isLoading,
    pageLoadKey: "help_center-page_self_serve_home",
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    dataUpdatedAt: dataUpdatedAt
  });
  var _useQueryParams = useQueryParams(supportSelfServeParams.parse),
    brandOrderToken = _useQueryParams.brand_order_token;
  var _useSkipNodeSelection = useSkipNodeSelection(),
    skipOrderSelection = _useSkipNodeSelection.skipOrderSelection,
    nodeSelected = _useSkipNodeSelection.nodeSelected,
    setNodeSelected = _useSkipNodeSelection.setNodeSelected;
  useEffect(function () {
    if (brandOrderToken) {
      setNodeSelected(true);
    }
  }, [brandOrderToken, setNodeSelected]);
  useEffect(function () {
    if (nodeSelected && level1Node) {
      goToNextStepRef.current({
        skipOrderSelection: skipOrderSelection
      });
      setNodeSelected(false);
    }
  }, [nodeSelected, level1Node, skipOrderSelection, setNodeSelected]);
  var handleNodeSelection = function handleNodeSelection(node) {
    setLevel1Node(node);
    setNodeSelected(true);
    trackCxFoundationsL1IssueClick(node.id, retailerOrBrandToken);
  };
  return /*#__PURE__*/React.createElement(Wrapper, {
    "data-test-id": "self-serve-home-wrapper"
  }, /*#__PURE__*/React.createElement(IssueTopicsWrapper, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "sectionHeaderSansMedium"
  }, /*#__PURE__*/React.createElement(StrictLocalMsg, {
    id: "x+eH0H",
    defaultMessage: [{
      "type": 0,
      "value": "Waar kunnen we je mee helpen?"
    }]
  })), /*#__PURE__*/React.createElement(Spacer, {
    height: "3x"
  }), /*#__PURE__*/React.createElement(IssueTopics, {
    parentNode: tree["Root"],
    selectedNode: level1Node,
    setSelectedNode: handleNodeSelection,
    loadTimingRef: loadTimingRef
  })));
};
var Wrapper = styled(Column).attrs({
  align: {
    mobileAndAbove: "center",
    tabletAndAbove: "normal"
  },
  justify: "space-between"
}).withConfig({
  displayName: "Home__Wrapper"
})(["", "{padding:", ";}", "{padding:", ";}"], responsiveMediaQueries.tabletAndAbove, getSpacing("4x", "5x"), responsiveMediaQueries.mobile, getSpacing("4x", "2x"));
var IssueTopicsWrapper = styled(Column).attrs({
  align: "flex-start"
}).withConfig({
  displayName: "Home__IssueTopicsWrapper"
})(["width:100%;"]);